import { useRef, useEffect } from 'react';

type Value = string | number | boolean | object | null | undefined;

/**
 * Returns the previous value of a given value (state, prop, etc.)
 * This is useful for comparing the current value with the previous value
 * to determine if a change has occurred.
 *
 * @param {Value} value - The value to track.
 * @returns {Value} - The previous value of the input.
 *
 * @example
 * const prevIsOpen = usePrevious(isOpen);
 * @example
 * const prevCount = usePrevious(count);
 * @example
 * const prevList = usePrevious(list);
 * @example
 * const prevPage = usePrevious(page);
 */

export default function usePrevious(value: Value) {
  const initialValue = useRef(value).current;
  const ref = useRef(initialValue);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}
